import { scssVariables, remCss, rgba } from 'src/helpers';

// Shared style tokens for FilePreviewBox and its sub-components.
// Values here are ported 1:1 from the original file-box.scss (template-1),
// so visuals stay pixel-identical after the class -> inline-style rewrite.
//
// Naming convention:
// - `xxxStyle`      -> safe to reuse as-is when this component is later copied to React Native
// - `xxxWebExtra`    -> web-only CSS props (box-shadow string, cursor, ...)
//                       that must be dropped/replaced when porting to React Native.
// Usage on web: style={{ ...xxxStyle, ...xxxWebExtra }}

export const colors = {
  primary: scssVariables['colors-mc-primary'],
  white: scssVariables['colors-mc-white'],
  inputPrimary: scssVariables['colors-mc-input-primary'],
  black1: scssVariables['colors-mc-black1']
};

const RADIUS = remCss(5); // scss: border-radius: rem(5)

// Outer box that wraps any file type (image / video / audio / dcm / other)
// scss: .mc-file-box.template-1 { border-radius: rem(5); box-shadow: ...; }
export const boxStyle = {
  position: 'relative',
  borderRadius: RADIUS,
  overflow: 'hidden',
  backgroundColor: 'white',
  flexShrink: 0
};
// web-only: RN needs shadowColor/shadowOffset/shadowOpacity/shadowRadius (iOS)
// + elevation (Android) instead of a single boxShadow string.
export const boxShadowWebExtra = {
  boxShadow: `0 0 ${remCss(10)}px ${rgba(colors.black1, 0.08)}`
};

// Inner content area (the clickable preview: image, video player, icon, ...)
export const fileAreaStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden'
};
// web-only: pointer cursor has no RN equivalent
export const fileAreaWebExtra = {
  cursor: 'pointer'
};

// Small "x" close button, top-right corner
// scss: .mc-file-box__close { position: absolute; right: 5%; top: 5%; }
export const closeButtonStyle = {
  position: 'absolute',
  top: '5%',
  right: '5%',
  zIndex: 2,
  padding: 0
};
// web-only: border/background reset + cursor only make sense for a <button> on web
export const closeButtonWebExtra = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer'
};

// Semi-transparent bar overlaid on the bottom of the preview, showing the
// file name (template-1 design).
// scss: .mc-file-box__detail_name { background-color: rgba(mc-input-primary, 0.3);
//         border-radius: 0 0 rem(5) rem(5); padding: 5%; text-align: center; color: white; }
export const detailOverlayStyle = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: rgba(colors.inputPrimary, 0.3),
  borderBottomLeftRadius: RADIUS,
  borderBottomRightRadius: RADIUS,
  padding: '5%'
};
export const detailOverlayWebExtra = {
  cursor: 'pointer'
};

// Row for DICOM "Save" / "View" action buttons, shown below the file name.
// Always a single row that shrinks to fit its content — no stacked/wrap variant.
export const dcmActionsStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: 6,
  marginTop: 6,
  alignSelf: 'center',
  flexShrink: 0
};

// Centered spinner overlay (used while downloading a generic file)
// Kept RN-safe: use marginLeft/marginTop (half of a known width/height) instead
// of `transform: 'translate(-50%, -50%)'`, which RN cannot parse as a string.
export const centerOverlayStyle = (size = 30) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginLeft: -size / 2,
  marginTop: -size / 2,
  zIndex: 3
});

// Small pill badge showing the countdown to a file's deleteAt.
// Shown regardless of showFileName — sits top-left, mirroring the close
// button which sits top-right (closeButtonStyle: top/right 5%).
export const countdownBadgeStyle = {
  paddingTop: remCss(3),
  paddingBottom: remCss(3),
  paddingLeft: remCss(3),
  paddingRight: remCss(3),
  borderRadius: remCss(3),
  backgroundColor: 'rgba(60, 60, 60, 0.55)',
  display: 'flex',
  flexDirection: 'row',
  gap: remCss(3),
  alignItems: 'center'
};
export const countdownBadgeStyleOverLay = {
  position: 'absolute',
  top: remCss(10),
  right: remCss(10),
  zIndex: 1
};
// --- Template 2: horizontal row layout (small thumbnail + name/size to the right) ---
// scss: .mc-file-box.template-2 { display: flex; flex-direction: row; gap: rem(10); position: relative; }
export const rowBoxStyle = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: remCss(10)
};

// scss: .mc-file-box__detail { flex: 1; justify-content: center; min-width: rem(230); }
export const rowDetailStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minWidth: remCss(200)
};
export const rowDetailWebExtra = {
  cursor: 'pointer'
};
